import React, { useState, useEffect } from 'react';
import Button from './Button';

interface StudentInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  studentNumber: number;
  studentEmail: string;
  studentPhone?: string;
  studentPoints: number;
  studentRoles: string[];
  scale?: number;
}

const StudentInfoDialog: React.FC<StudentInfoDialogProps> = ({
  isOpen,
  onClose,
  studentName,
  studentNumber,
  studentEmail,
  studentPhone = '010-1234-1234',
  studentPoints,
  studentRoles,
  scale = 1
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Pretendard',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--Color-background-normal, white)',
        borderRadius: '16px',
        width: isMobile ? 'calc(100% - 40px)' : '450px',
        maxWidth: isMobile ? '100%' : '450px',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transformOrigin: 'center center',
        transform: `scale(${scale})`,
        position: 'relative'
      }}>
        {/* 메인 컨텐츠 */}
        <div style={{
          width: isMobile ? 'calc(100% - 32px)' : '394px',
          margin: isMobile ? '16px' : '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '32px' : '64px'
        }}>
          {/* 상단 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '28px'
          }}>
            {/* 헤더 영역 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '20px' : '40px'
            }}>
              {/* 헤더 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <div style={{
                  flex: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <h1 style={{
                    margin: 0,
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '600',
                    lineHeight: isMobile ? '27px' : '30px',
                    color: 'var(--Color-text-normal, #161616)',
                    wordWrap: 'break-word'
                  }}>
                    학생 정보
                  </h1>
                </div>
                
                {/* 닫기 버튼 */}
                <div style={{
                  padding: '4px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={onClose}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img 
                      src="/images/icons/closeIcon.svg" 
                      alt="닫기"
                      style={{
                        width: '13.20px',
                        height: '13.20px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* 정보 필드들 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '16px' : '24px'
              }}>
                {/* 첫 번째 행: 이름, 번호 */}
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: isMobile ? '16px' : '24px'
                }}>
                  {/* 학생 이름 */}
                  <div style={{
                    width: isMobile ? '100%' : '185px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <label style={{
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600',
                      lineHeight: isMobile ? '19.5px' : '21px',
                      color: 'var(--Color-text-assistive, #989898)',
                      wordWrap: 'break-word'
                    }}>
                      학생 이름
                    </label>
                    <span style={{
                      fontSize: isMobile ? '15px' : '16px',
                      fontWeight: '500',
                      lineHeight: isMobile ? '22.5px' : '24px',
                      color: 'var(--Color-text-normal, #161616)',
                      wordWrap: 'break-word'
                    }}>
                      {studentName}
                    </span>
                  </div>

                  {/* 번호 */}
                  <div style={{
                    width: isMobile ? '100%' : '185px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <label style={{
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600',
                      lineHeight: isMobile ? '19.5px' : '21px',
                      color: 'var(--Color-text-assistive, #989898)',
                      wordWrap: 'break-word'
                    }}>
                      번호
                    </label>
                    <span style={{
                      fontSize: isMobile ? '15px' : '16px',
                      fontWeight: '500',
                      lineHeight: isMobile ? '22.5px' : '24px',
                      color: 'var(--Color-text-normal, #161616)',
                      wordWrap: 'break-word'
                    }}>
                      {studentNumber}번
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 두 번째 행: 이메일, 전화번호 */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'flex-start',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '16px' : '24px'
            }}>
              {/* 이메일 */}
              <div style={{
                width: isMobile ? '100%' : '185px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <label style={{
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  lineHeight: isMobile ? '19.5px' : '21px',
                  color: 'var(--Color-text-assistive, #989898)',
                  wordWrap: 'break-word'
                }}>
                  이메일
                </label>
                <span style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '500',
                  lineHeight: isMobile ? '22.5px' : '24px',
                  color: 'var(--Color-text-normal, #161616)',
                  wordWrap: 'break-word'
                }}>
                  {studentEmail}
                </span>
              </div>

              {/* 전화번호 */}
              <div style={{
                width: isMobile ? '100%' : '185px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <label style={{
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  lineHeight: isMobile ? '19.5px' : '21px',
                  color: 'var(--Color-text-assistive, #989898)',
                  wordWrap: 'break-word'
                }}>
                  전화번호
                </label>
                <span style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '500',
                  lineHeight: isMobile ? '22.5px' : '24px',
                  color: 'var(--Color-text-normal, #161616)',
                  wordWrap: 'break-word'
                }}>
                  {studentPhone}
                </span>
              </div>
            </div>

            {/* 포인트 */}
            <div style={{
              width: isMobile ? '100%' : '185px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <label style={{
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                lineHeight: isMobile ? '19.5px' : '21px',
                color: 'var(--Color-text-assistive, #989898)',
                wordWrap: 'break-word'
              }}>
                포인트
              </label>
              <span style={{
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '500',
                lineHeight: isMobile ? '22.5px' : '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                {studentPoints}점
              </span>
            </div>

            {/* 역할 */}
            <div style={{
              width: isMobile ? '100%' : '185px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <label style={{
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                lineHeight: isMobile ? '19.5px' : '21px',
                color: 'var(--Color-text-assistive, #989898)',
                wordWrap: 'break-word'
              }}>
                역할
              </label>
              <span style={{
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '500',
                lineHeight: isMobile ? '22.5px' : '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                {studentRoles.length > 0 ? studentRoles.join(', ') : '없음'}
              </span>
            </div>
          </div>

          {/* 완료 버튼 */}
          <Button
            width="100%"
            height={isMobile ? "40px" : "45px"}
            variant="submit"
            onClick={onClose}
          >
            완료
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StudentInfoDialog;

